import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../Common/Button';
import { SlideOver } from '../Common/SlideOver';
import { showToast } from '../../hooks/useToast';

interface Props {
  open: boolean;
  schedule?: any;
  onClose: () => void;
  onSaved: () => void;
}

export function ScheduleForm({ open, schedule, onClose, onSaved }: Props) {
  const [reports, setReports] = useState<any[]>([]);
  const [reportId, setReportId] = useState<string>('');
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timeUtc, setTimeUtc] = useState('14:00');
  const [recipientsText, setRecipientsText] = useState('');
  const [exportFormat, setExportFormat] = useState<'xlsx' | 'pdf'>('xlsx');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.listSavedReports().then(r => setReports(r.reports));
    }
    if (schedule) {
      setReportId(schedule.reportId);
      setName(schedule.name);
      setFrequency(schedule.frequency);
      setDayOfWeek(schedule.dayOfWeek ?? 1);
      setDayOfMonth(schedule.dayOfMonth ?? 1);
      setTimeUtc(schedule.timeUtc?.substring(0, 5) ?? '14:00');
      const recip = typeof schedule.recipients === 'string' ? JSON.parse(schedule.recipients) : schedule.recipients;
      setRecipientsText((recip ?? []).join(', '));
      setExportFormat(schedule.exportFormat);
    } else {
      setReportId('');
      setName('');
      setFrequency('weekly');
      setDayOfWeek(1);
      setDayOfMonth(1);
      setTimeUtc('14:00');
      setRecipientsText('');
      setExportFormat('xlsx');
    }
  }, [open, schedule]);

  const save = async () => {
    const recipients = recipientsText
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    if (recipients.length === 0) {
      showToast('error', 'Add at least one recipient');
      return;
    }
    const invalid = recipients.filter(r => !/^.+@(laorthos\.com|unitymsk\.com)$/i.test(r));
    if (invalid.length > 0) {
      showToast('error', `Recipients must be @laorthos.com or @unitymsk.com: ${invalid.join(', ')}`);
      return;
    }
    if (!reportId) {
      showToast('error', 'Select a report');
      return;
    }

    setBusy(true);
    try {
      const body = {
        reportId, name, frequency,
        dayOfWeek:  frequency === 'weekly' ? dayOfWeek : null,
        dayOfMonth: frequency === 'monthly' ? dayOfMonth : null,
        timeUtc,
        recipients,
        exportFormat,
      };
      if (schedule) {
        await api.updateSchedule(schedule.id, body);
      } else {
        await api.createSchedule(body);
      }
      showToast('success', schedule ? 'Schedule updated' : 'Schedule created');
      onSaved();
      onClose();
    } catch (err: any) {
      showToast('error', err.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const inputClass = 'w-full px-2.5 py-1.5 text-sm bg-surface border border-border rounded-md focus:outline-none focus:border-accent';

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={schedule ? 'Edit schedule' : 'New schedule'}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
      </>}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-text-hint mb-1">Report</label>
          <select value={reportId} onChange={e => setReportId(e.target.value)} className={inputClass}>
            <option value="">Select a report…</option>
            {reports.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-text-hint mb-1">Schedule name</label>
          <input value={name} onChange={e => setName(e.target.value)} className={inputClass} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-text-hint mb-1">Frequency</label>
            <select value={frequency} onChange={e => setFrequency(e.target.value as any)} className={inputClass}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-text-hint mb-1">Time (UTC)</label>
            <input type="time" value={timeUtc} onChange={e => setTimeUtc(e.target.value)} className={inputClass} />
          </div>
        </div>

        {frequency === 'weekly' && (
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-text-hint mb-1">Day of week</label>
            <select value={dayOfWeek} onChange={e => setDayOfWeek(Number(e.target.value))} className={inputClass}>
              <option value={0}>Sunday</option>
              <option value={1}>Monday</option>
              <option value={2}>Tuesday</option>
              <option value={3}>Wednesday</option>
              <option value={4}>Thursday</option>
              <option value={5}>Friday</option>
              <option value={6}>Saturday</option>
            </select>
          </div>
        )}
        {frequency === 'monthly' && (
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-text-hint mb-1">Day of month (1–28)</label>
            <input type="number" min={1} max={28} value={dayOfMonth} onChange={e => setDayOfMonth(Number(e.target.value))} className={inputClass} />
          </div>
        )}

        <div>
          <label className="block text-[11px] uppercase tracking-wide text-text-hint mb-1">Recipients (comma-separated)</label>
          <textarea
            value={recipientsText}
            onChange={e => setRecipientsText(e.target.value)}
            rows={3}
            placeholder="rcm@laorthos.com, billing@unitymsk.com"
            className={inputClass + ' resize-none'}
          />
          <div className="text-[10px] text-text-hint mt-1">Only @laorthos.com and @unitymsk.com addresses are accepted.</div>
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wide text-text-hint mb-1">Format</label>
          <select value={exportFormat} onChange={e => setExportFormat(e.target.value as any)} className={inputClass}>
            <option value="xlsx">Excel (xlsx)</option>
            <option value="pdf">PDF</option>
          </select>
        </div>
      </div>
    </SlideOver>
  );
}
